// PaymentForm.js - Premium Design
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PAYMENT_METHODS } from '../../utils/constants';
import { formatCurrency } from '../../utils/helpers';
import WebXPayModal from '../auth/WebXPayModal';

const stripePromise = loadStripe('pk_test_51SRcOyHXUx0CYTJlf66kgdd1RPgfxfM6zzTfQoI4MNQ8h16yDJpz1tG63TfqlxntQumdLoGlrTWZ8DqmgIsdqNdm00hdwLq9yq');

const StripePaymentForm = ({
  clientSecret,
  amount,
  currency,
  customerName,
  customerEmail,
  onSuccess,
  onError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const card = elements.getElement(CardElement);
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: card,
        billing_details: { name: customerName, email: customerEmail },
      }
    });
    setProcessing(false);
    if (error) onError(error.message);
    else if (paymentIntent.status === 'succeeded') onSuccess(paymentIntent);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#ffffff',
                '::placeholder': { color: '#4b5563' },
                fontFamily: 'Inter, sans-serif',
              },
              invalid: { color: '#ef4444' },
            },
            hidePostalCode: false,
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {['VISA', 'MC', 'AMEX'].map(m => (
            <span key={m} className="px-2 py-0.5 bg-white/10 text-[10px] text-white rounded font-black tracking-widest">{m}</span>
          ))}
        </div>
        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Secured by Stripe</span>
      </div>
      <button
        type="submit"
        disabled={!stripe || processing}
        className={`w-full py-4 rounded-full font-black text-sm tracking-widest uppercase transition-all shadow-xl ${
          !stripe || processing ? 'bg-white/5 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/20 active:scale-95'
        }`}
      >
        {processing ? 'Processing...' : `Pay ${formatCurrency(amount, currency)}`}
      </button>
    </form>
  );
};

const PaymentForm = ({
  selectedVehicle,
  searchData,
  extrasData,
  extrasTotal = 0,
  onPayment,
  onBack,
  processing,
  bookingDetails,
  onPaymentSuccess,
  onPaymentError,
  priceResult,
  submitRequest = false,
  onSubmitRequestHandled = () => { },
  onCanSubmitChange = () => { }
}) => {
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.WEBXPAY);
  const [paymentOption, setPaymentOption] = useState('full');
  const [customAmount, setCustomAmount] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showWebXPayModal, setShowWebXPayModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);

  const baseAmount = priceResult?.baseFare || priceResult?.basePrice || 0;
  const finalAmount = priceResult?.total || 0;
  const depositAmount = Math.ceil(finalAmount * 0.2 * 100) / 100;

  const getPaymentAmount = () => {
    if (paymentOption === 'full') return finalAmount;
    if (paymentOption === 'deposit') return depositAmount;
    if (paymentOption === 'custom') return customAmount || depositAmount;
    return finalAmount;
  };

  const handleCustomAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    if (amount >= depositAmount && amount <= finalAmount) setCustomAmount(amount);
  };

  const handleWebXPaySuccess = (result) => {
    setShowWebXPayModal(false);
    if (onPaymentSuccess) onPaymentSuccess(result);
  };

  const handleWebXPayError = (errorMsg) => {
    setShowWebXPayModal(false);
    if (onPaymentError) onPaymentError(errorMsg);
  };

  const handleWebXPayClose = () => {
    setShowWebXPayModal(false);
    setModalDismissed(true);
  };

  const handleSubmit = async () => {
    if (!termsAccepted) {
      alert('Please accept the Terms & Conditions');
      return;
    }
    const paymentAmount = getPaymentAmount();
    const paymentData = {
      method: paymentMethod,
      amount: paymentAmount,
      fullAmount: finalAmount,
      depositOnly: paymentOption !== 'full',
      paymentType: paymentOption,
      currency: selectedVehicle?.pricing?.currency || 'USD'
    };

    if (paymentMethod === PAYMENT_METHODS.WEBXPAY) {
      try {
        setModalDismissed(false);
        await onPayment(paymentData);
      } catch (error) {
        alert('Failed to initiate WebXPay. Please try again.');
      }
      return;
    }
    onPayment(paymentData);
    if (onSubmitRequestHandled) onSubmitRequestHandled();
  };

  useEffect(() => {
    if (submitRequest && !processing) handleSubmit();
  }, [submitRequest]);

  useEffect(() => {
    onCanSubmitChange(!processing && termsAccepted);
  }, [processing, termsAccepted]);

  useEffect(() => {
    if (bookingDetails?.id && paymentMethod === PAYMENT_METHODS.WEBXPAY && !bookingDetails.id.toString().startsWith('temp_') && !processing && !showWebXPayModal && !modalDismissed) {
      setShowWebXPayModal(true);
    }
  }, [bookingDetails, paymentMethod, processing, showWebXPayModal]);

  const labelClasses = "block text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-4 pl-1";

  if (bookingDetails?.clientSecret && paymentMethod === PAYMENT_METHODS.CARD) {
    return (
      <div className="space-y-8">
        <div className="glass-panel bg-green-500/5 border-green-500/20 rounded-2xl p-6 flex items-center gap-4 animate-in fade-in slide-in-from-top-4">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xl">✓</div>
          <div>
            <h3 className="font-black text-xs text-green-500 uppercase tracking-widest">Booking Created</h3>
            <p className="text-[10px] text-gray-500 mt-1 font-bold">#{bookingDetails.code} — Please complete payment below</p>
          </div>
        </div>

        <div className="glass-panel p-6 rounded-3xl border-white/5">
          <div className="flex justify-between items-end mb-6">
            <div>
              <div className="text-[10px] text-blue-500 font-black uppercase tracking-widest">Authorized Amount</div>
              <div className="text-3xl font-black text-white tracking-tighter mt-1">{formatCurrency(bookingDetails.paymentAmount, bookingDetails.currency)}</div>
            </div>
            {bookingDetails.paymentType !== 'full' && (
              <div className="text-right">
                <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest">Balance Later</div>
                <div className="text-sm font-bold text-gray-400">{formatCurrency(bookingDetails.totalAmount - bookingDetails.paymentAmount, bookingDetails.currency)}</div>
              </div>
            )}
          </div>
          <Elements stripe={stripePromise}>
            <StripePaymentForm
              clientSecret={bookingDetails.clientSecret}
              amount={bookingDetails.paymentAmount}
              currency={bookingDetails.currency}
              customerName={bookingDetails.customerName}
              customerEmail={bookingDetails.customerEmail}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
            />
          </Elements>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-12">
        {/* Step Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center text-blue-500 text-xl font-black">4</div>
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight italic uppercase italic">Secure Checkout</h2>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Select your preferred payment arrangement</p>
          </div>
        </div>

        {/* Amount Selection & Slider */}
        <div className="space-y-8">
          <div className="flex items-center justify-between px-1">
            <label className={labelClasses}>Payment Arrangement</label>
            <div className="flex gap-2">
              {['full', 'deposit', 'custom'].map(opt => (
                <button 
                  key={opt}
                  onClick={() => setPaymentOption(opt)}
                  className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${paymentOption === opt ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-panel p-8 rounded-[2rem] border-white/5 bg-white/[0.02] relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <svg className="w-24 h-24 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>
            </div>

            <div className="relative z-10 space-y-8">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                  <div className="text-[10px] text-blue-500 font-black uppercase tracking-widest mb-1 italic">Amount to pay now</div>
                  <div className="text-5xl font-black text-white tracking-tighter flex items-baseline gap-1">
                    <span className="text-2xl text-blue-500/50 italic mr-1">{selectedVehicle?.pricing?.currency || '$'}</span>
                    {getPaymentAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                
                {paymentOption !== 'full' && (
                  <div className="text-right">
                    <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">Remaining Balance</div>
                    <div className="text-xl font-bold text-gray-400 italic">
                      {formatCurrency(Math.max(0, finalAmount - getPaymentAmount()), selectedVehicle?.pricing?.currency)}
                    </div>
                  </div>
                )}
              </div>

              {/* Slider & Manual Input for Custom Amount */}
              <div className="space-y-6 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Select or Enter Amount</span>
                    <span className="text-[8px] text-blue-500/50 font-bold uppercase mt-0.5">Min: {formatCurrency(depositAmount, selectedVehicle?.pricing?.currency || 'USD')}</span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-blue-500/50">{selectedVehicle?.pricing?.currency || '$'}</span>
                    <input 
                      type="number"
                      value={getPaymentAmount().toFixed(2)}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val)) {
                          setPaymentOption('custom');
                          setCustomAmount(Math.min(finalAmount, Math.max(0, val)));
                        }
                      }}
                      className="w-32 h-9 bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 text-[12px] font-mono font-black text-white focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                </div>
                <div className="flex justify-between text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  <span>Minimum (20%)</span>
                  <span className="text-blue-500">{( (getPaymentAmount() / finalAmount) * 100 ).toFixed(0)}% Selected</span>
                  <span>Full (100%)</span>
                </div>
                
                <div className="relative h-12 flex items-center">
                  <div className="absolute w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-300"
                      style={{ width: `${(getPaymentAmount() / finalAmount) * 100}%` }}
                    ></div>
                  </div>
                  <input 
                    type="range"
                    min={depositAmount}
                    max={finalAmount}
                    step={0.01}
                    value={getPaymentAmount()}
                    onChange={(e) => {
                      setPaymentOption('custom');
                      setCustomAmount(parseFloat(e.target.value));
                    }}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer z-20 slider-thumb-premium"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <button onClick={() => setPaymentOption('deposit')} className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${paymentOption === 'deposit' ? 'bg-blue-600/10 border-blue-600 text-blue-500' : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/10'}`}>20% Deposit</button>
                  <button onClick={() => { setPaymentOption('custom'); setCustomAmount(finalAmount * 0.5); }} className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${paymentOption === 'custom' && Math.abs(customAmount - finalAmount * 0.5) < 1 ? 'bg-blue-600/10 border-blue-600 text-blue-500' : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/10'}`}>50% Partial</button>
                  <button onClick={() => setPaymentOption('full')} className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${paymentOption === 'full' ? 'bg-blue-600/10 border-blue-600 text-blue-500' : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/10'}`}>Full Amount</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="space-y-6">
          <label className={labelClasses}>Select Payment Method</label>
            {/* WebXPay Option (Single Selection as Card is removed) */}
            <div 
              className="col-span-1 md:col-span-2 glass-panel p-6 rounded-[1.5rem] border border-green-500/50 bg-green-500/5 ring-1 ring-green-500/20 relative overflow-hidden group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center font-black text-white text-[10px] shadow-lg shadow-green-500/20">WEBX</div>
                <div className="flex-1">
                  <div className="text-[11px] font-black text-white uppercase tracking-widest">WebXPay Payment Gateway</div>
                  <div className="text-[8px] text-green-500 font-black uppercase tracking-[0.2em] mt-0.5">Secure Checkout Enabled</div>
                </div>
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px]">✓</div>
              </div>
              <p className="text-[10px] text-gray-500 font-medium leading-relaxed italic">Default payment method for local and international cards, bank transfers, and digital wallets (Genie/Frimi).</p>
            </div>
        </div>

        {/* Terms & CTA */}
        <div className="space-y-8 pt-4">
          <label className="group flex items-start gap-4 p-5 bg-white/5 rounded-2xl border border-white/5 cursor-pointer hover:bg-white/[0.08] transition-all">
            <div className="relative flex items-center mt-0.5">
              <input 
                type="checkbox" 
                checked={termsAccepted} 
                onChange={(e) => setTermsAccepted(e.target.checked)} 
                className="w-5 h-5 rounded-lg border-white/20 bg-white/5 text-blue-600 focus:ring-blue-600 focus:ring-offset-0 transition-all checked:bg-blue-600" 
              />
            </div>
            <span className="text-[11px] text-gray-500 font-medium leading-relaxed group-hover:text-gray-400 transition-colors">
              I certify that I have reviewed the <a href="/refund-policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-500 font-black hover:underline underline-offset-4 decoration-blue-500/30">Cancellation Policy</a> and agree to the <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-blue-500 font-black hover:underline underline-offset-4 decoration-blue-500/30">Terms of Service</a> guiding this reservation.
            </span>
          </label>

          <div className="flex flex-col md:flex-row gap-4">
            <button 
              onClick={onBack} 
              className="px-8 py-5 border border-white/10 rounded-full font-black text-[10px] tracking-widest uppercase text-gray-500 hover:text-white hover:bg-white/5 transition-all active:scale-95"
            >
              ← Back to step 3
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={processing || !termsAccepted} 
              className={`flex-1 py-5 rounded-full font-black text-xs tracking-widest uppercase transition-all shadow-[0_20px_40px_rgba(0,0,0,0.3)] ${processing || !termsAccepted ? 'bg-white/5 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/30 active:scale-[0.98]'}`}
            >
              {processing ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  Processing...
                </span>
              ) : `Complete ${formatCurrency(getPaymentAmount(), selectedVehicle?.pricing?.currency)} Payment`}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider-thumb-premium::-webkit-slider-thumb {
          -webkit-appearance: none;
          height: 32px;
          width: 32px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 6px solid #1a1a1a;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.3);
          transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .slider-thumb-premium::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          background: #60a5fa;
        }
        .slider-thumb-premium::-webkit-slider-thumb:active {
          transform: scale(0.95);
        }
      `}</style>

      {showWebXPayModal && (
        <WebXPayModal
          booking={{
            id: bookingDetails?.id || `temp_${Date.now()}`,
            code: bookingDetails?.code || `BKG${Date.now()}`,
            amount: getPaymentAmount()
          }}
          totalAmount={getPaymentAmount()}
          currency={selectedVehicle?.pricing?.currency || 'LKR'}
          onSuccess={handleWebXPaySuccess}
          onError={handleWebXPayError}
          onClose={handleWebXPayClose}
        />
      )}
    </>
  );
};

export default PaymentForm;